<script setup>
import { differenceInWeeks, getYear } from "date-fns";
import _ from "lodash";
import { computed, reactive, ref, toRefs, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { getInternshipSectionTitle } from "@/components/ExperiencePage/utils.js";
import JSONtoHTML from "@/components/forms/SharedComponents/WYSIWYG/JSONtoHTML.vue";
import {
  UMassAndCongresoCities,
  UMassCities,
  UMassOrHomeSchoolCities,
} from "@/components/program-manager/sessions/components/Internships/constants.js";
import { joinArrayItems } from "@/mixins/helpers.js";
import internshipService from "@/services/internship";
import { scrollToTarget } from "../../../util/scrollToTarget";
import InternshipPage from "../InternshipSection/InternshipPage.vue";
import ShowMore from "../ShowMore/ShowMore.vue";

const router = useRouter();
const route = useRoute();

const props = defineProps({
  internshipsObj: {
    type: Object,
    required: true,
    default: () => {},
  },
  session: {
    type: Object,
    required: true,
    default: () => {},
  },
  program: {
    type: Object,
    required: true,
    default: () => {},
  },
  externalLink: {
    type: String,
    default: "",
  },
  registerType: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["observeSection", "applyClicked"]);
const { internshipsObj, session, program } = toRefs(props);

const showPage = ref(false);
let totalPages = 0;
const urlName = "internship_page";
const isPages = ref(false);
const items = ref([]);
const pageParam = ref(router?.currentRoute?.value?.query?.internship_page ?? 1);
const mainCardDiv = ref(null);
const scrollDuration = 600;

const handleShowLess = (eventData) => {
  showLessData(eventData);
  scrollToTarget(mainCardDiv, scrollDuration);
};

function showMoreData(page) {
  pageParam.value = page;
  const newItems = chunkedInternshipArray.value[page - 1];
  items.value = [...items.value, ...newItems];
}

function showLessData(page) {
  pageParam.value = page;
  const newItems = chunkedInternshipArray.value[page - 1];
  items.value = [...newItems];
}

function updateShowMore(bool) {
  isPages.value = bool;
}

const chunkedInternshipArray = ref([]);

const internships = reactive({
  items: [],
  count: 0,
  end: 0,
  start: 0,
});

const getQualifications = (academicLevels, skills, languages) => {
  const concatAcademicLevels = joinArrayItems(academicLevels, "academic_year");
  const concatSkills = joinArrayItems(skills, "value");
  const concatLanguages = joinArrayItems(languages, "proficiency_level", "value", "");

  const qualificationsCopy = [concatAcademicLevels, concatSkills, concatLanguages].filter(Boolean).join(", ");

  if (qualificationsCopy) return "Open to " + qualificationsCopy;
  else return "";
};

const isAnyUMass = computed(() => {
  return isUMass.value || isUMassAndCongreso.value || isUMassOrHomeSchool.value;
});

const arrivalCityId = computed(() => session?.value?.session_travel_details[0]?.arrival_city?.id?.toString());

const isUMass = computed(() => Object.keys(UMassCities).includes(arrivalCityId.value));

const isUMassAndCongreso = computed(() => Object.keys(UMassAndCongresoCities).includes(arrivalCityId.value));

const isUMassOrHomeSchool = computed(() => Object.keys(UMassOrHomeSchoolCities).includes(arrivalCityId.value));

const weeks = computed(() => {
  return differenceInWeeks(
    new Date(session?.value?.session_travel_details?.departure_date),
    new Date(session?.value?.session_travel_details?.arrival_date),
  );
});

const year = computed(() => {
  return getYear(new Date(session?.value?.session_travel_details?.arrival_date));
});

const internshipSectionTitle = computed(() => getInternshipSectionTitle(session?.value));

const semesterItems = () => {
  const termsNoCopy = [];
  termsNoCopy.push(session?.value?.terms?.[0]);

  session?.value?.terms?.forEach((term) => {
    if (termsNoCopy.every((noCopy) => noCopy.session !== term.session)) termsNoCopy.push(term);
  });

  return joinArrayItems(termsNoCopy, "session");
};

const cityCountrySession = computed(() => {
  const city = session?.value?.arrival_city?.city_ascii ?? "";
  const country = session?.value?.arrival_city?.country?.name ?? "";

  return { city, country };
});

const getInterships = async (newInternships) => {
  const cityArray = [];
  const termsArray = [];
  newInternships?.cities?.forEach((obj) => {
    cityArray.push(String(obj?.city?.city_ascii));
  });
  newInternships?.terms?.forEach((obj) => {
    termsArray.push(String(obj?.id));
  });
  const params = {
    limit: 30,
    cities: cityArray,
    terms: termsArray,
    durations: newInternships?.durations ?? [],
    status: "Active",
  };
  const response = await internshipService.getInternships(params);
  const data = response?.data?.data ?? {};
  const mapItems = data?.items
    ? data.items.map((item) => {
        return {
          ...item,
          program_page: {
            id: item.id,
            name: item.title,
          },
          name: item.title,
          industry: joinArrayItems(item?.industries, "career_pathway"),
          qualifications: getQualifications(item.academic_levels, item.needed_skills, item.required_languages),
          weeks: weeks.value,
          semester: semesterItems(),
          year: year.value,
          program_id: program?.value?.id,
          session_id: session?.value?.id,
          internship_id: item?.id,
          session: {
            session_type: { name: "Internship" },
            terms: session?.value?.terms ?? [],
            session_travel_details: session?.value?.session_travel_details,
            academic_start_date: session?.value?.academic_start_date ?? "",
            academic_end_date: session?.value?.academic_end_date ?? "",
            academic_start_date_finalized: session?.value?.academic_start_date_finalized,
            academic_end_date_finalized: session?.value?.academic_end_date_finalized,
            application_deadline: session?.value?.application_deadline ?? "",
          },
          cityCountrySession: cityCountrySession.value,
        };
      })
    : [];

  internships.items = mapItems;
  internships.count = data?.count ?? 0;
  internships.end = data?.end ?? 0;
  internships.start = data?.start ?? 0;

  chunkedInternshipArray.value = _.chunk(internships.items, 3);
  totalPages = chunkedInternshipArray.value.length;

  if (pageParam.value > chunkedInternshipArray.value.length) {
    pageParam.value = 1;
    await router.push({
      query: { ...route.query, internship_page: pageParam.value },
    });
  }

  if (isNaN(pageParam.value)) {
    pageParam.value = 1;
    await router.push({
      query: { ...route.query, internship_page: pageParam.value },
    });
  }
  isPages.value = pageParam.value >= chunkedInternshipArray.value.length ? true : false;
  items.value = _.flatten(_.slice(chunkedInternshipArray.value, 0, pageParam.value));
};

watch(
  internshipsObj,
  async (newInternships) => {
    if (newInternships.length !== 0) {
      showPage.value = true;
      await getInterships(newInternships);
    }
  },
  { immediate: true, deep: true },
);

watch([() => showPage.value, () => session.value, () => internships], ([newShowPage, newSession, newInternships]) => {
  if (newShowPage && newSession?.internship_filter_criteria && newInternships) {
    emit("observeSection", "internships");
  }
});
</script>

<template>
  <div
    v-if="showPage && session?.internship_filter_criteria && internships"
    ref="mainCardDiv"
    class="text-indigo-base pt-20 px-4 md:px-8"
  >
    <div class="text-4xl mt-4 mb-4 font-bold text-indigo-base">
      <div class="text-2xl" data-testid="internship-section-title">
        {{ internshipSectionTitle }}
      </div>
      <div data-testid="internship-section-subtitle">
        OPPORTUNITIES
      </div>
    </div>
    <div class="space-y-4 block h-fit w-full mb-8 text-indigo-base">
      <JSONtoHTML
        v-if="!!session?.internships_description"
        :tiptap-json="session?.internships_description"
        class="text-lg font-medium text-left mt-4 mb-2"
        data-testid="internships-description"
      />
    </div>
    <InternshipPage
      :session="session"
      :display-internship="items"
      :register-type="props.registerType"
      :external-link="props.externalLink"
      @apply-clicked="clickedInternshipId => { emit('applyClicked', clickedInternshipId) }"
    />
    <ShowMore
      v-if="chunkedInternshipArray"
      :total-pages="totalPages"
      :current-page="pageParam"
      :is-pages="isPages"
      :url-param-name="urlName"
      @page-change="showMoreData"
      @show-less="handleShowLess"
      @show-bool="updateShowMore"
    />
    <hr class="my-1 border-b-3 md:mt-20 border-gray-200" />
  </div>
</template>
